import React, { useEffect, useState } from "react";
import { Card, Button, Space, Tooltip, Divider, Input, Form } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import "./tandc.css";
import { UserService } from "../../../../Services/Manage/UserService";
import { LetterService } from "../../../../Services/Manage/LetterService";
import useMessage from "antd/es/message/useMessage";

const TandC = ({ initialData = null, handleHomeSeg }) => {
  const [form] = Form.useForm();
  const [message, msgContx] = useMessage();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: "Start typing your T&C…" }),
    ],
    content: initialData ? initialData.data : "",
  });

  // Set form values for editing
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({ tags: initialData.tag });
      if (editor) editor.commands.setContent(initialData.data);
    } else {
      form.resetFields();
      if (editor) editor.commands.setContent("");
    }
  }, [initialData, form, editor]);

  if (!editor) return null;

// Handle save / update
const handleSave = async () => {
  const tags = form.getFieldValue("tags") || "";
  const htmlContent = editor.getHTML();

  if (!tags.trim() || !htmlContent.trim()) {
    message.warning("Both Tag and Content are required!");
    return;
  }

  const user = await UserService.getOmeUser();

  const result = {
    tag: tags,
    data: htmlContent,
    added_by: user.uid,
  };

  let request;

  if (initialData && initialData.id) {
    // EDIT: run updateTC
    request = await LetterService.updateTC(result, initialData.id);
    if (request.status) {
      message.success(request.message);
      handleHomeSeg();
    } else {
      message.error(request.message);
    }
  } else {
    // CREATE: run createTC
    request = await LetterService.createTC(result);
    if (request.status) {
      message.success(request.message);
      form.resetFields();
      handleHomeSeg();
      if (editor) editor.commands.setContent("");
    } else {
      message.error(request.message);
    }
  }

};


  return (
    <Form form={form} layout="vertical">
      {/* Tag Input */}
      <Form.Item name="tags" label="Tags" rules={[{ required: true, message: "Tag is required" }]}>
        <Input
          size="large"
          placeholder="This is for sale, quotation"
          showCount
          maxLength={30}
        />
      </Form.Item>

      {/* Editor Toolbar */}
      <div
        style={{
          padding: "6px 8px",
          borderBottom: "1px solid #e0e0e0",
          borderRadius: "6px 6px 0 0",
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        {[1, 2, 3].map((level) => (
          <Tooltip key={level} title={`Heading ${level}`}>
            <Button
              size="large"
              type={editor.isActive("heading", { level }) ? "primary" : "default"}
              onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
            >
              H{level}
            </Button>
          </Tooltip>
        ))}

        <Tooltip title="Bold">
          <Button
            size="large"
            type={editor.isActive("bold") ? "primary" : "default"}
            icon={<BoldOutlined />}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
        </Tooltip>

        <Tooltip title="Italic">
          <Button
            size="large"
            type={editor.isActive("italic") ? "primary" : "default"}
            icon={<ItalicOutlined />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
        </Tooltip>

        <Tooltip title="Underline">
          <Button
            size="large"
            type={editor.isActive("underline") ? "primary" : "default"}
            icon={<UnderlineOutlined />}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
        </Tooltip>

        <Tooltip title="Bullet List">
          <Button
            size="large"
            type={editor.isActive("bulletList") ? "primary" : "default"}
            icon={<UnorderedListOutlined />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
        </Tooltip>

        <Tooltip title="Numbered List">
          <Button
            size="large"
            type={editor.isActive("orderedList") ? "primary" : "default"}
            icon={<OrderedListOutlined />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
        </Tooltip>
      </div>

      {/* Editor Area */}
      <div
        style={{
          minHeight: 400,
          padding: 14,
          fontSize: 15,
          lineHeight: 1.7,
          background: "white",
          border: "1px solid #e0e0e0",
          borderTop: "none",
          borderRadius: "0 0 6px 6px",
        }}
      >
<EditorContent editor={editor} className="tandc-content" />

      </div>
      {msgContx}
      {/* Save / Update Button */}
      <Divider style={{ margin: "16px 0" }} />
      <Space>
        <Button
          size="large"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
        >
          {initialData ? "Update" : "Save"}
        </Button>
      </Space>
    </Form>
  );
};

export default TandC;
